import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { DatePicker } from "@/components/ui/date-picker";
import { MultiSelect } from "@/components/ui/multiselect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getCurrentUser,
  getUsersByPermission,
} from "../../api/services/userService";
import { useApp } from "../../contexts/AppContext";
import { useScenarioAccess } from "../../hooks/useScenarioAccess";
import { useScenarios } from "../../hooks/useScenarios";
import { ComprehensiveScenario, ScenarioPriority } from "../../types/scenarios";

// Share Scenario Modal
interface ShareModalProps {
  scenario: ComprehensiveScenario;
  onClose: () => void;
}

const DEMO_USERS = [
  { id: "linda", name: "Linda Kowalski", role: "CFO" },
  { id: "sarah", name: "Sarah Mitchell", role: "Finance Manager" },
  { id: "marcus", name: "Marcus Patel", role: "Regional Manager" },
  { id: "catherine", name: "Catherine Hughes", role: "CEO" },
  { id: "jennifer", name: "Jennifer Adams", role: "Accountant" },
];

export const ShareScenarioModal: React.FC<ShareModalProps> = ({
  scenario,
  onClose,
}) => {
  const { shareScenario, updateShare, removeShare } = useScenarios();
  const { mode } = useApp();
  const isLive = mode === "live";
  const { access } = useScenarioAccess(scenario);
  // Users who can access the scenario purely through inherited, status-conditional
  // access (never an explicit share). Shown read-only — inherited access is derived
  // and cannot be removed here.
  const inheritedOnly = access.filter(
    (a) => a.isInherited && !a.isExplicitShare && !a.isOwner,
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [permission, setPermission] = useState<"view" | "comment" | "edit">(
    "view",
  );
  const [isSharing, setIsSharing] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: liveUsersData,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery({
    queryKey: ["users-by-permission", "view_scenarios"],
    queryFn: () => getUsersByPermission("view_scenarios"),
    enabled: isLive,
  });

  const { data: currentUserData } = useQuery({
    queryKey: ["currentUser"],
    queryFn: getCurrentUser,
    enabled: isLive,
  });

  const users =
    isLive && liveUsersData
      ? liveUsersData.users
          .filter((u) => u.userId !== currentUserData?.id)
          .map((u) => ({ id: u.userId, name: u.name, role: u.roleName }))
      : DEMO_USERS;

  // Users without an explicit share — already-shared users are managed in the
  // "Currently Shared" list above, so they cannot be picked again here.
  const shareableUsers = users.filter(
    (u) => !scenario.shares.some((s) => s.userId === u.id),
  );
  const userOptions = shareableUsers.map((u) => ({
    value: u.id,
    label: `${u.name} - ${u.role}`,
  }));

  const handleShare = async () => {
    if (selectedUserIds.length === 0 || isSharing) return;

    setIsSharing(true);
    try {
      for (const userId of selectedUserIds) {
        const user = users.find((u) => u.id === userId);
        if (!user) continue;
        await shareScenario(scenario.id, {
          userId: user.id,
          userName: user.name,
          permission,
        });
      }
      setSelectedUserIds([]);
    } catch (error) {
      console.error("Failed to share scenario:", error);
    } finally {
      setIsSharing(false);
    }
  };

  // Share mutations happen outside React Query, so refresh the scenario caches
  // on close — otherwise the Sharing tab behind the modal renders stale shares.
  // The tab's access list comes from vw_ScenarioAccess over GraphQL, keyed by
  // numeric scenario id, so that cache needs invalidating as well.
  const handleClose = () => {
    queryClient.invalidateQueries({ queryKey: ["scenarios"] });
    queryClient.invalidateQueries({ queryKey: ["scenarioViews"] });
    queryClient.invalidateQueries({ queryKey: ["scenarioViewsCount"] });
    queryClient.invalidateQueries({
      queryKey: ["scenarioAccess", Number(scenario.id)],
    });
    queryClient.invalidateQueries({ queryKey: ["scenario-share-users"] });
    queryClient.invalidateQueries({
      queryKey: ["scenarioAuditLog", Number(scenario.id)],
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Share "{scenario.name}"
          </h2>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Currently Shared */}
          {scenario.shares.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Currently Shared ({scenario.shares.length})
              </h3>
              <div className="space-y-2">
                {scenario.shares.map((share) => (
                  <div
                    key={share.userId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {share.userName}
                      </p>
                      <p className="text-sm text-gray-500">
                        Can{" "}
                        {share.permission === "view"
                          ? "View"
                          : share.permission === "comment"
                            ? "Comment"
                            : "Edit"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={share.permission}
                        onValueChange={(value) =>
                          updateShare(
                            scenario.id,
                            share.userId,
                            value as "view" | "comment" | "edit",
                          )
                        }
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">Can View</SelectItem>
                          <SelectItem value="comment">Can Comment</SelectItem>
                          <SelectItem value="edit">Can Edit</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => removeShare(scenario.id, share.userId)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove access"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inherited access — read-only, cannot be removed */}
          {inheritedOnly.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Also has access ({inheritedOnly.length})
              </h3>
              <div className="space-y-2">
                {inheritedOnly.map((entry) => (
                  <div
                    key={entry.userId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {entry.userName}
                      </p>
                      <p className="text-sm text-gray-500">Can View</p>
                    </div>
                    <span className="text-xs font-medium text-gray-600 bg-gray-200 rounded px-2 py-1">
                      Inherited
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add People */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Add People
            </h3>
            {isLive && usersLoading ? (
              <p className="text-sm text-gray-500 py-2">Loading users...</p>
            ) : isLive && usersError ? (
              <p className="text-sm text-red-600 py-2">
                Failed to load users. Please try again later.
              </p>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1">
                  <MultiSelect
                    options={userOptions}
                    value={selectedUserIds}
                    onValueChange={setSelectedUserIds}
                    placeholder="Search and select people…"
                    emptyMessage="No users found."
                  />
                </div>
                <Select
                  value={permission}
                  onValueChange={(value) =>
                    setPermission(value as "view" | "comment" | "edit")
                  }
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">Can View</SelectItem>
                    <SelectItem value="comment">Can Comment</SelectItem>
                    <SelectItem value="edit">Can Edit</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  onClick={handleShare}
                  disabled={selectedUserIds.length === 0 || isSharing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSharing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSharing ? "Adding…" : "Add"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// A calendar date carries no time — pin it to midday UTC so the stored instant
// lands on the same calendar day in every timezone, rather than rolling back a
// day when local midnight is converted to UTC (e.g. BST).
function toUtcNoon(date: Date): Date {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12),
  );
}

// Park Scenario Modal
interface ParkModalProps {
  scenario: ComprehensiveScenario;
  onClose: () => void;
  /** Called after the scenario is successfully parked (e.g. to show a toast). */
  onParked?: () => void;
}

export const ParkScenarioModal: React.FC<ParkModalProps> = ({
  scenario,
  onClose,
  onParked,
}) => {
  const { parkScenario } = useScenarios();
  const [reason, setReason] = useState("");
  const [reviewDate, setReviewDate] = useState<Date | undefined>(undefined);
  const [isParking, setIsParking] = useState(false);

  const isActive = scenario.status === "active";

  const handlePark = async () => {
    if (!reason || !reviewDate || isParking) return;
    setIsParking(true);
    try {
      await parkScenario(scenario.id, reason, toUtcNoon(reviewDate));
      onParked?.();
      onClose();
    } catch (error) {
      console.error("Failed to park scenario:", error);
      toast.error("Unable to park scenario", {
        description:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setIsParking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Park Scenario</h2>
          <p className="text-sm text-gray-600 mt-1">"{scenario.name}"</p>
        </div>

        <div className="p-6 space-y-4">
          {isActive && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> This scenario is currently active.
                Parking it will remove it from active use.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Why are you parking this scenario? *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="e.g., Defer until acquisition complete"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review Date *
            </label>
            <DatePicker
              className="w-full"
              value={reviewDate}
              onChange={(date) => setReviewDate(date ?? undefined)}
              disabledDate={(d) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return d < today;
              }}
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Parked scenarios are hidden from the main
              list but can be reactivated at any time.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isParking}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handlePark}
            disabled={!reason || !reviewDate || isParking}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isParking ? "Parking…" : "Park Scenario"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Set Priority Modal
interface SetPriorityModalProps {
  scenario: ComprehensiveScenario;
  onClose: () => void;
}

export const SetPriorityModal: React.FC<SetPriorityModalProps> = ({
  scenario,
  onClose,
}) => {
  const { setPriority } = useScenarios();
  const [selectedPriority, setSelectedPriority] = useState<ScenarioPriority>(
    scenario.priority,
  );

  const priorities: {
    value: ScenarioPriority;
    label: string;
    color: string;
    icon: string;
  }[] = [
    {
      value: "critical",
      label: "Critical",
      color: "bg-red-100 text-red-700 border-red-200",
      icon: "🔴",
    },
    {
      value: "high",
      label: "High",
      color: "bg-orange-100 text-orange-700 border-orange-200",
      icon: "🟠",
    },
    {
      value: "medium",
      label: "Medium",
      color: "bg-yellow-100 text-yellow-700 border-yellow-200",
      icon: "🟡",
    },
    {
      value: "low",
      label: "Low",
      color: "bg-green-100 text-green-700 border-green-200",
      icon: "🟢",
    },
    {
      value: "none",
      label: "No Priority",
      color: "bg-gray-100 text-gray-700 border-gray-200",
      icon: "⚪",
    },
  ];

  const handleSetPriority = () => {
    setPriority(scenario.id, selectedPriority);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Set Priority</h2>
          <p className="text-sm text-gray-600 mt-1">"{scenario.name}"</p>
        </div>

        <div className="p-6 space-y-2">
          {priorities.map((priority) => (
            <button
              key={priority.value}
              onClick={() => setSelectedPriority(priority.value)}
              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                selectedPriority === priority.value
                  ? `${priority.color} border-current`
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{priority.icon}</span>
                <span className="font-medium">{priority.label}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSetPriority}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Set Priority
          </button>
        </div>
      </div>
    </div>
  );
};

// Approval Decision Modal
interface ApprovalModalProps {
  scenario: ComprehensiveScenario;
  onClose: () => void;
}

export const ApprovalDecisionModal: React.FC<ApprovalModalProps> = ({
  scenario,
  onClose,
}) => {
  const { approveScenario, rejectScenario, requestChanges } = useScenarios();
  const [action, setAction] = useState<"approve" | "reject" | "changes">(
    "approve",
  );
  const [comments, setComments] = useState("");

  const handleSubmit = () => {
    if (action === "approve") {
      approveScenario(scenario.id, comments || undefined);
      toast.success("Scenario approved", {
        description: `"${scenario.name}" has been approved.`,
      });
    } else if (action === "reject") {
      if (!comments) {
        alert("Please provide a reason for rejection");
        return;
      }
      rejectScenario(scenario.id, comments);
      toast.error("Scenario rejected", {
        description: `"${scenario.name}" has been rejected.`,
      });
    } else if (action === "changes") {
      if (!comments) {
        alert("Please describe what changes are needed");
        return;
      }
      requestChanges(scenario.id, comments);
      toast.success("Changes requested", {
        description: `"${scenario.name}" has been returned to draft status.`,
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Approval Decision
          </h2>
          <p className="text-sm text-gray-600 mt-1">"{scenario.name}"</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Action Selection */}
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setAction("approve")}
              className={`p-4 rounded-lg border-2 text-center transition-all ${
                action === "approve"
                  ? "bg-green-50 border-green-500 text-green-700"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="text-3xl mb-2">✓</div>
              <div className="font-medium">Approve</div>
            </button>
            <button
              onClick={() => setAction("changes")}
              className={`p-4 rounded-lg border-2 text-center transition-all ${
                action === "changes"
                  ? "bg-yellow-50 border-yellow-500 text-yellow-700"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="text-3xl mb-2">↩</div>
              <div className="font-medium">Request Changes</div>
            </button>
            <button
              onClick={() => setAction("reject")}
              className={`p-4 rounded-lg border-2 text-center transition-all ${
                action === "reject"
                  ? "bg-red-50 border-red-500 text-red-700"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="text-3xl mb-2">✗</div>
              <div className="font-medium">Reject</div>
            </button>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {action === "approve"
                ? "Comments (optional)"
                : action === "changes"
                  ? "What changes are needed? *"
                  : "Reason for rejection *"}
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder={
                action === "approve"
                  ? "Add any comments..."
                  : action === "changes"
                    ? "Describe the changes needed..."
                    : "Explain why you are rejecting this scenario..."
              }
            />
          </div>

          {/* Preview */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
            <p className="text-sm text-gray-600">
              {action === "approve" &&
                "This scenario will be marked as Approved and the requester will be notified."}
              {action === "changes" &&
                "This scenario will return to Draft status and the requester will be notified of the requested changes."}
              {action === "reject" &&
                "This scenario will be marked as Rejected and the requester will be notified."}
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 py-2 rounded-lg font-medium text-white ${
              action === "approve"
                ? "bg-green-600 hover:bg-green-700"
                : action === "changes"
                  ? "bg-yellow-600 hover:bg-yellow-700"
                  : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {action === "approve"
              ? "Approve"
              : action === "changes"
                ? "Request Changes"
                : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Submit for Approval Modal
interface SubmitForApprovalModalProps {
  scenario: ComprehensiveScenario;
  onClose: () => void;
}

export const SubmitForApprovalModal: React.FC<SubmitForApprovalModalProps> = ({
  scenario,
  onClose,
}) => {
  const { updateScenario, requestApproval } = useScenarios();
  const { mode } = useApp();
  const [selectedApprover, setSelectedApprover] = useState("");
  const [comment, setComment] = useState("");

  const isLive = mode === "live";

  const approvers = [
    { id: "cfo", name: "Linda Kowalski", role: "CFO" },
    { id: "ceo", name: "Catherine Hughes", role: "CEO" },
    { id: "manager", name: "Sarah Mitchell", role: "Finance Manager" },
  ];

  const handleSubmit = async () => {
    if (isLive) {
      // Live mode: backend resolves workflow automatically
      const resultStatus = await requestApproval(scenario.id, []);
      if (resultStatus === "active") {
        toast.success("Scenario activated", {
          description:
            "No approval workflow is configured — scenario has been marked Active directly.",
        });
      } else if (resultStatus) {
        toast.success("Submitted for approval", {
          description: `"${scenario.name}" has been submitted to the approval workflow.`,
        });
      }
      onClose();
      return;
    }

    // Demo mode: existing logic
    if (!selectedApprover) {
      alert("Please select an approver");
      return;
    }

    const approver = approvers.find((a) => a.id === selectedApprover);
    if (!approver) return;

    // Update scenario status and assign approver
    updateScenario(scenario.id, {
      status: "pending-approval",
      assignedApprover: approver.name,
    });

    toast.success("Submitted for approval", {
      description: `"${scenario.name}" has been submitted to ${approver.name} for approval.`,
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Submit for Approval
          </h2>
          <p className="text-sm text-gray-600 mt-1">"{scenario.name}"</p>
        </div>

        <div className="p-6 space-y-4">
          {isLive ? (
            /* Live mode: no approver dropdown — backend resolves workflow */
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Submit "{scenario.name}" for approval?</strong>
              </p>
              <p className="text-sm text-blue-700 mt-1">
                The system will route this scenario to the appropriate approval
                workflow based on its category and net impact.
              </p>
            </div>
          ) : (
            /* Demo mode: hardcoded approver dropdown */
            <>
              {/* Select Approver */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Approver *
                </label>
                <select
                  value={selectedApprover}
                  onChange={(e) => setSelectedApprover(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Choose an approver...</option>
                  {approvers.map((approver) => (
                    <option key={approver.id} value={approver.id}>
                      {approver.name} ({approver.role})
                    </option>
                  ))}
                </select>
              </div>

              {/* Optional Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (optional)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Add any context or notes for the approver..."
                />
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> The selected approver will be notified
                  and can approve or request changes to this scenario.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isLive && !selectedApprover}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit for Approval
          </button>
        </div>
      </div>
    </div>
  );
};

// Export aliases for backward compatibility
export { ShareScenarioModal as ShareModal };
export { ParkScenarioModal as ParkModal };
